// Recuperación de Traslados — re-reads the original CFDI XMLs and restores the
// line items that intake destroyed.
//
// The broker itemized every invoice: chofer / gasolina / peaje / camión, each its
// own concepto with its own importe. NewService.js:186 folds every concepto
// sharing a service_type into ONE service_items row and joins their descriptions
// with " + ", so all four collapsed into an opaque line reading
// "TRASLADO MONTERREY/CDMX + GASOLINA + PEAJE + CAMION". A year of itemized
// invoices became one number.
//
// The XMLs are still in incoming-invoices/YYYY/MM/DD/<uuid>.xml, so the real
// split is recoverable exactly -- no estimation.
//
// Review is per invoice, and every concepto is classified BY HAND. The regex only
// proposes a default. Auto-classification is what filed a PEAJE under "Reparación
// Eléctrica"; the point of this screen is to stop trusting a guess that looks like
// a fact.
//
// Amounts are stored SIN IVA (concepto.importe, not concepto.total). IVA is
// acreditable, so the real cost is the subtotal -- and the per-km rate card it
// gets compared against is likewise pre-tax. Mixing bases would inflate the
// broker's apparent cost by 16%.
import { React, useState, useMemo } from "react";
import AppWrapper from "../AppWrapper";
import {
  Page,
  Button,
  BlockStack,
  InlineStack,
  Text,
  LegacyCard,
  Banner,
  Badge,
  ProgressBar,
  Select,
  Spinner,
  Checkbox,
  Divider,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { parseCfdiInvoice } from "../../utility/cfdiParser";
import { formatPrice, formatPlate } from "../../utility/funcs";

const TRASLADO_TYPE_ID = 11;

// "chofer" is the only negotiable line -- it's what an in-house driver replaces.
// Everything else priced here is a real traslado cost and counts toward the total.
// "excluir" is the escape hatch for a line that isn't a traslado cost at all
// (e.g. an oil change sharing an invoice with a trip fee): it is never inserted.
const KINDS = [
  { label: "Chofer", value: "chofer" },
  { label: "Gasolina", value: "gasolina" },
  { label: "Peaje", value: "peaje" },
  { label: "Regreso (camión/vuelo)", value: "regreso" },
  { label: "Comida", value: "comida" },
  { label: "Verificación", value: "verificacion" },
  { label: "Otro costo del traslado", value: "otro" },
  { label: "No es del traslado — ignorar", value: "excluir" },
];

// Everything except chofer (tracked separately) and excluir (never stored).
const PASSTHROUGH_KINDS = [
  "gasolina",
  "peaje",
  "regreso",
  "comida",
  "verificacion",
  "otro",
];

const norm = (s) =>
  (s || "")
    .toString()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

// Proposes a default only. A human confirms every line.
function propose(descripcion) {
  const d = norm(descripcion);
  if (/\bVERIFICACION\b/.test(d)) return "verificacion";
  if (/\b(TRASLADO|RECOLECCION|RECOLECION|ENTREGA)\b/.test(d)) return "chofer";
  if (/\b(GASOLINA|COMBUSTIBLE|DIESEL)\b/.test(d)) return "gasolina";
  if (/\b(PEAJE|CASETA|CASETAS)\b/.test(d)) return "peaje";
  if (/\b(CAMION|AUTOBUS|VUELO|AVION|BOLETO|PASAJE)\b/.test(d)) return "regreso";
  if (/\b(COMIDA|ALIMENTO|ALIMENTOS|VIATICO|VIATICOS)\b/.test(d)) return "comida";
  return null; // unproposed -- forces a human decision
}

function extractRoute(descripcion) {
  const d = norm(descripcion);
  const m = d.match(
    /(?:TRASLADO|RECOLECCION|RECOLECION|ENTREGA)\s+([A-Z\s]+?)\s*\/\s*([A-Z\s]+?)(?:\s*\+|\s*$)/,
  );
  if (!m) return { origen: null, destino: null };
  return { origen: m[1].trim() || null, destino: m[2].trim() || null };
}

// "RECOLECCION X/Y" = bused OUT to X, drove the car back to Y.
// "TRASLADO X/Y"    = drove out to Y, bused home.
// Changes which end the return fare applies to.
function extractDirection(descripcion) {
  return /\b(RECOLECCION|RECOLECION)\b/.test(norm(descripcion))
    ? "recoleccion"
    : "traslado";
}

function TrasladoRecovery() {
  const [phase, setPhase] = useState("idle"); // idle|analyzing|review|saving|saved
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [invoices, setInvoices] = useState([]); // { ...service, conceptos: [], discarded }
  const [failures, setFailures] = useState([]);
  const [error, setError] = useState(null);
  const [saveMsg, setSaveMsg] = useState(null);
  const [onlyPending, setOnlyPending] = useState(false);

  const analyze = async () => {
    setPhase("analyzing");
    setError(null);
    setInvoices([]);
    setFailures([]);

    const { data: items, error: itemsErr } = await supabase
      .from("service_items")
      .select(
        "service, services!inner(id, invoice, invoice_id, performed_at, total, car, provider, odometer)",
      )
      .eq("type", TRASLADO_TYPE_ID);

    if (itemsErr) {
      setError("No se pudieron leer los servicios: " + itemsErr.message);
      setPhase("idle");
      return;
    }

    const svcById = {};
    for (const it of items || []) {
      const s = it.services;
      if (s && s.invoice) svcById[s.id] = s;
    }
    const services = Object.values(svcById).sort(
      (a, b) => new Date(b.performed_at) - new Date(a.performed_at),
    );

    // Plates, so a human can tell what they're looking at.
    const carIds = [...new Set(services.map((s) => s.car).filter(Boolean))];
    const plateById = {};
    if (carIds.length) {
      const { data: cars } = await supabase
        .from("cars")
        .select("id, plate, model:car_models(name, brand:car_brands(name))")
        .in("id", carIds);
      for (const c of cars || [])
        plateById[c.id] = {
          plate: c.plate,
          model: `${c.model?.brand?.name || ""} ${c.model?.name || ""}`.trim(),
        };
    }

    setProgress({ done: 0, total: services.length });
    const out = [];
    const bad = [];

    for (let i = 0; i < services.length; i++) {
      const s = services[i];
      // Mirror of the write path in NewService.js:206-215.
      const path =
        s.performed_at.split("T")[0].replace(/-/g, "/") + "/" + s.invoice + ".xml";
      try {
        const { data: signed, error: signErr } = await supabase.storage
          .from("incoming-invoices")
          .createSignedUrl(path, 1800);
        if (signErr || !signed?.signedUrl)
          throw new Error("XML no encontrado en storage");
        const res = await fetch(signed.signedUrl);
        if (!res.ok) throw new Error("HTTP " + res.status);
        const cfdi = parseCfdiInvoice(await res.text());
        if (!cfdi.conceptos?.length) throw new Error("CFDI sin conceptos");

        let origen = null;
        let destino = null;
        let direction = null;
        let kmDeclarado = null;

        const conceptos = cfdi.conceptos.map((c, idx) => {
          const proposed = propose(c.descripcion);
          if (proposed === "chofer" && !origen) {
            const r = extractRoute(c.descripcion);
            origen = r.origen;
            destino = r.destino;
            direction = extractDirection(c.descripcion);
            const km = norm(c.descripcion).match(/KM\s*([0-9]+)/);
            if (km) kmDeclarado = Number(km[1]);
          }
          return {
            key: `${s.id}-${idx}`,
            descripcion: c.descripcion,
            amount: c.importe, // SIN IVA on purpose
            proposed,
            kind: proposed ?? "",
          };
        });

        out.push({
          serviceId: s.id,
          fecha: s.performed_at.split("T")[0],
          folio: s.invoice_id,
          car: s.car,
          plate: plateById[s.car]?.plate || (s.car ? `#${s.car}` : "sin auto"),
          model: plateById[s.car]?.model || "",
          provider: s.provider,
          servicesTotal: s.total,
          conceptos,
          origen,
          destino,
          direction,
          kmDeclarado,
          discarded: false,
        });
      } catch (e) {
        bad.push({
          serviceId: s.id,
          fecha: s.performed_at.split("T")[0],
          path,
          msg: e?.message || String(e),
        });
      }
      setProgress({ done: i + 1, total: services.length });
    }

    setInvoices(out);
    setFailures(bad);
    setPhase("review");
  };

  const setKind = (serviceId, key, kind) =>
    setInvoices((prev) =>
      prev.map((inv) =>
        inv.serviceId !== serviceId
          ? inv
          : {
              ...inv,
              conceptos: inv.conceptos.map((c) =>
                c.key === key ? { ...c, kind } : c,
              ),
            },
      ),
    );

  // The pass-throughs repeat verbatim across all 92 invoices ("GASOLINA",
  // "PEAJE", "CAMION"). Classify one, apply to every identical description.
  // The chofer lines are unique (plate + km + route), so they're never swept up
  // by this -- they get read individually.
  const applyToIdentical = (descripcion, kind) => {
    const target = norm(descripcion);
    let n = 0;
    setInvoices((prev) =>
      prev.map((inv) => ({
        ...inv,
        conceptos: inv.conceptos.map((c) => {
          if (norm(c.descripcion) === target && c.kind !== kind) {
            n++;
            return { ...c, kind };
          }
          return c;
        }),
      })),
    );
    return n;
  };

  const toggleDiscard = (serviceId) =>
    setInvoices((prev) =>
      prev.map((inv) =>
        inv.serviceId === serviceId
          ? { ...inv, discarded: !inv.discarded }
          : inv,
      ),
    );

  const active = invoices.filter((i) => !i.discarded);
  const discardedCount = invoices.length - active.length;

  const pendingCount = active.reduce(
    (a, inv) => a + inv.conceptos.filter((c) => !c.kind).length,
    0,
  );

  const totals = useMemo(() => {
    const t = {};
    for (const inv of invoices) {
      if (inv.discarded) continue;
      for (const c of inv.conceptos) {
        if (!c.kind || c.kind === "excluir") continue;
        t[c.kind] = (t[c.kind] || 0) + c.amount;
      }
    }
    return t;
  }, [invoices]);

  const choferTotal = totals.chofer || 0;
  const passTotal = PASSTHROUGH_KINDS.reduce((a, k) => a + (totals[k] || 0), 0);
  const grand = choferTotal + passTotal;

  const save = async () => {
    setPhase("saving");
    setError(null);
    let orders = 0;
    let expenses = 0;

    for (const inv of active) {
      const keep = inv.conceptos.filter((c) => c.kind && c.kind !== "excluir");
      if (!keep.length || !inv.car) continue;

      const choferAmount = keep
        .filter((c) => c.kind === "chofer")
        .reduce((a, c) => a + c.amount, 0);
      const subtotal = keep.reduce((a, c) => a + c.amount, 0);

      const { data: order, error: oErr } = await supabase
        .from("traslado_orders")
        .insert({
          car: inv.car,
          origin_label: inv.origen,
          dest_label: inv.destino,
          status: "historical",
          driver: inv.provider,
          driver_extra: choferAmount || null,
          actual_total: subtotal,
          scheduled_at: inv.fecha,
          service: inv.serviceId,
          notes:
            `Recuperado del CFDI ${inv.folio || ""}. Dirección: ${inv.direction || "?"}.` +
            (inv.kmDeclarado ? ` KM en factura: ${inv.kmDeclarado}.` : "") +
            " Importes SIN IVA. Clasificado a mano.",
        })
        .select("id")
        .single();

      if (oErr) {
        setError(`Error guardando servicio ${inv.serviceId}: ${oErr.message}`);
        setPhase("review");
        return;
      }
      orders++;

      const rows = keep
        .filter((c) => c.kind !== "chofer")
        .map((c) => ({
          id: crypto.randomUUID(),
          traslado_order: order.id,
          kind: c.kind,
          amount: c.amount,
          source: "cfdi_concepto",
          notes: c.descripcion,
        }));

      if (rows.length) {
        const { error: eErr } = await supabase
          .from("traslado_expenses")
          .insert(rows);
        if (eErr) {
          setError(`Error guardando gastos de ${inv.serviceId}: ${eErr.message}`);
          setPhase("review");
          return;
        }
        expenses += rows.length;
      }
    }

    setSaveMsg(
      `${orders} traslados y ${expenses} gastos guardados. ${discardedCount} factura(s) descartadas.`,
    );
    setPhase("saved");
  };

  const visible = onlyPending
    ? invoices.filter(
        (inv) => !inv.discarded && inv.conceptos.some((c) => !c.kind),
      )
    : invoices;

  return (
    <AppWrapper>
      <Page
        title="Recuperación de Traslados"
        subtitle="Relee los XML originales y restaura las partidas que la importación colapsó"
        backAction={{ url: "/services" }}
      >
        <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}

          {phase === "idle" && (
            <Banner tone="info">
              <p>
                Las facturas del bróker sí venían desglosadas (chofer, gasolina,
                peaje, camión). La importación las unió en una sola línea. Esto lee
                los XML originales y recupera el desglose real — sin estimar nada.
              </p>
              <p style={{ marginTop: 8 }}>
                Se revisa factura por factura. El sistema sólo propone; tú
                confirmas cada concepto. Puedes descartar facturas completas que no
                sean traslados. No se guarda nada hasta que lo apruebes.
              </p>
            </Banner>
          )}

          {phase === "analyzing" && (
            <LegacyCard sectioned>
              <BlockStack gap="200">
                <InlineStack gap="200" blockAlign="center">
                  <Spinner size="small" />
                  <Text>
                    Leyendo XML {progress.done} de {progress.total}…
                  </Text>
                </InlineStack>
                <ProgressBar
                  progress={
                    progress.total
                      ? Math.round((progress.done / progress.total) * 100)
                      : 0
                  }
                />
              </BlockStack>
            </LegacyCard>
          )}

          {failures.length > 0 && (
            <Banner tone="warning">
              {failures.length} factura(s) no se pudieron leer:{" "}
              {failures
                .slice(0, 3)
                .map((f) => `#${f.serviceId} (${f.msg})`)
                .join(", ")}
              {failures.length > 3 ? "…" : ""}
            </Banner>
          )}

          {invoices.length > 0 && (
            <>
              <LegacyCard sectioned>
                <BlockStack gap="300">
                  <Text variant="headingMd">Desglose recuperado (sin IVA)</Text>
                  <InlineStack gap="600" wrap>
                    <BlockStack>
                      <Text tone="subdued" variant="bodySm">
                        Chofer — lo único negociable
                      </Text>
                      <Text variant="heading2xl">{formatPrice(choferTotal)}</Text>
                      <Text tone="subdued" variant="bodySm">
                        {grand ? Math.round((choferTotal / grand) * 100) : 0}% del
                        costo de traslado
                      </Text>
                    </BlockStack>
                    {PASSTHROUGH_KINDS.filter((k) => totals[k]).map((k) => (
                      <BlockStack key={k}>
                        <Text tone="subdued" variant="bodySm">
                          {KINDS.find((x) => x.value === k)?.label}
                        </Text>
                        <Text variant="headingLg">{formatPrice(totals[k])}</Text>
                      </BlockStack>
                    ))}
                    <BlockStack>
                      <Text tone="subdued" variant="bodySm">
                        Total traslados
                      </Text>
                      <Text variant="headingLg">{formatPrice(grand)}</Text>
                    </BlockStack>
                  </InlineStack>
                  <Text tone="subdued" variant="bodySm">
                    Gasolina, peaje, comida, regreso y verificación se pagan igual
                    sin importar quién maneje — no cambian al internalizar al
                    chofer. El ahorro sólo puede salir de la línea de chofer.
                  </Text>
                </BlockStack>
              </LegacyCard>

              <InlineStack gap="300" blockAlign="center">
                <Checkbox
                  label={`Sólo facturas con pendientes (${pendingCount} conceptos)`}
                  checked={onlyPending}
                  onChange={setOnlyPending}
                />
                <Text tone="subdued" variant="bodySm">
                  {active.length} activas · {discardedCount} descartadas
                </Text>
              </InlineStack>

              <BlockStack gap="300">
                {visible.map((inv) => (
                  <LegacyCard key={inv.serviceId}>
                    <LegacyCard.Section>
                      <InlineStack align="space-between" blockAlign="center">
                        <InlineStack gap="300" blockAlign="center">
                          <Text
                            variant="headingSm"
                            tone={inv.discarded ? "subdued" : undefined}
                          >
                            {inv.fecha} · {formatPlate(inv.plate)}
                          </Text>
                          <Text tone="subdued" variant="bodySm">
                            {inv.model}
                          </Text>
                          {inv.origen ? (
                            <Badge>{`${inv.origen} / ${inv.destino || "?"}`}</Badge>
                          ) : (
                            <Badge tone="attention">Sin ruta</Badge>
                          )}
                          <Text tone="subdued" variant="bodySm">
                            Factura {inv.folio} · {formatPrice(inv.servicesTotal)}{" "}
                            c/IVA
                          </Text>
                          {inv.discarded && (
                            <Badge tone="critical">Descartada</Badge>
                          )}
                        </InlineStack>
                        <Button
                          size="slim"
                          tone={inv.discarded ? undefined : "critical"}
                          onClick={() => toggleDiscard(inv.serviceId)}
                        >
                          {inv.discarded
                            ? "Reactivar"
                            : "Descartar — no es traslado"}
                        </Button>
                      </InlineStack>
                    </LegacyCard.Section>
                    {!inv.discarded && (
                      <LegacyCard.Section>
                        <BlockStack gap="200">
                          {inv.conceptos.map((c, i) => (
                            <div key={c.key}>
                              {i > 0 && <Divider />}
                              <InlineStack
                                gap="300"
                                blockAlign="center"
                                wrap={false}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <Text variant="bodySm">{c.descripcion}</Text>
                                </div>
                                <div
                                  style={{
                                    width: 110,
                                    flexShrink: 0,
                                    textAlign: "right",
                                  }}
                                >
                                  <Text variant="bodySm">
                                    {formatPrice(c.amount)}
                                  </Text>
                                </div>
                                <div style={{ width: 240, flexShrink: 0 }}>
                                  <Select
                                    label=""
                                    labelHidden
                                    options={[
                                      { label: "— Elegir —", value: "" },
                                      ...KINDS,
                                    ]}
                                    value={c.kind}
                                    onChange={(v) =>
                                      setKind(inv.serviceId, c.key, v)
                                    }
                                  />
                                </div>
                                <div style={{ width: 150, flexShrink: 0 }}>
                                  {c.kind ? (
                                    <Button
                                      size="micro"
                                      onClick={() =>
                                        applyToIdentical(c.descripcion, c.kind)
                                      }
                                    >
                                      Aplicar a iguales
                                    </Button>
                                  ) : (
                                    <Badge tone="critical">Sin clasificar</Badge>
                                  )}
                                </div>
                              </InlineStack>
                            </div>
                          ))}
                        </BlockStack>
                      </LegacyCard.Section>
                    )}
                  </LegacyCard>
                ))}
              </BlockStack>
            </>
          )}

          {saveMsg && <Banner tone="success">{saveMsg}</Banner>}

          <InlineStack gap="300">
            <Button
              variant="primary"
              loading={phase === "analyzing"}
              disabled={phase === "analyzing" || phase === "saving"}
              onClick={analyze}
            >
              {invoices.length ? "Volver a analizar" : "Analizar facturas"}
            </Button>
            {phase === "review" && (
              <Button
                tone="success"
                variant="primary"
                loading={phase === "saving"}
                disabled={pendingCount > 0}
                onClick={save}
              >
                {pendingCount
                  ? `Faltan ${pendingCount} conceptos por clasificar`
                  : `Guardar ${active.length} traslados`}
              </Button>
            )}
          </InlineStack>
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default TrasladoRecovery;
